import { useEffect, useMemo, useRef, useState } from "react";
import {
  heroPartKey,
  overlayFitFor,
  overlayFitKeyFor,
  overlayFitKeys,
  overlayFitOn,
  overlayAllowed,
  overlayDirKeys,
  autoOverlayFit,
  HERO_ANCHOR,
  ANCHOR_REF,
  type AttachKind,
  OVERLAY_FIT_LIMITS,
  OVERLAY_HIDDEN,
  type AssetSpec,
  type OverlayCategory,
  type OverlayDir,
  type OverlayFit,
  type OverlayFitDoc,
} from "@greg-chick/shared";
import {
  loadManifest,
  loadOverlayFit,
  publishOverlayFit,
  type Manifest,
  type ManifestEntry,
} from "../lib/contentApi";
import { useRegisterSave } from "../lib/saveBar";
import { SlotUploader } from "./SlotUploader";
import { measureArtBox } from "../lib/measureArt";
import { saveFailSay } from "../lib/saveFail";

/**
 * 모자·소품 **놓을 자리 맞추기**(FR-HERO-01).
 *
 * 예전엔 이 값들이 코드에 박혀 있어서, 새 모자를 올리면 개발자가 숫자를 고쳐야 했다.
 * 여기서 눈으로 보며 맞추고 게시하면 코드 기본값 위에 덮인다.
 *
 * ## 미리보기를 **실제 크기(80px)** 로 먼저 보여주는 이유
 * 512px 원본으로 보고 맞췄다가 두 번 틀렸다 — 크게 보면 멀쩡한데 월드에서는
 * 모자가 머리를 덮어 버린다. 그래서 왼쪽이 실제 크기, 오른쪽이 확대다.
 *
 * ## 「모두」와 「이 아이만」
 * 아이마다 머리 크기·턱 높이가 달라서(턱 높이 ±11.7%) 자리는 **자동으로 보정된다**
 * (`hero:anchors` 로 아트에서 잰 값). 그래서 보통은 「모두」에서 한 번만 맞추면 된다.
 *
 * 자동 보정이 못 잡는 게 있다 — 머리·가슴 **위치**만 보기 때문에 옷 실루엣(망토 위 가방
 * 같은 것)까지는 모른다. 그런 몇 개만 「이 아이만」으로 마무리한다. 아이 값은 자동 보정을
 * **대체**한다(겹쳐 얹지 않는다) — 운영자는 이미 그 아이를 보면서 맞췄으니까.
 *
 * ## 네 아이를 나란히 보여주는 이유
 * 한 아이만 보고 맞추면 나머지 셋이 망가진 걸 모른다. 「모두」를 만질 때는 네 아이가
 * 동시에 바뀌므로, 그 자리에서 확인할 수 있어야 한다.
 */

const DIRS: { id: OverlayDir; label: string }[] = [
  { id: "down", label: "앞모습" },
  { id: "side", label: "옆모습" },
  { id: "up", label: "뒷모습" },
];
const BODIES = ["b1", "b2", "b3", "b4"] as const;
const ITEM_LABEL: Record<string, string> = {
  c1: "탐험가 모자", c2: "니트 비니", c3: "신라 금관", c4: "리본 머리띠",
  a1: "크로스백", a2: "별 배지", a3: "손등 랜턴",
  // 완주 메달은 소품 칸(a4)이 아니라 **보상 뱃지**(hero.badge.medal)다 — 랠리를 끝내면 자동으로 걸린다.
  medal: "완주 메달",
};
const PREVIEW_PX = 80; // 월드에서 히어로가 그려지는 실제 세로 px(DEFAULT_WORLD_SIZE.hero)

const BODY_LABEL: Record<string, string> = { b1: "첫째", b2: "둘째", b3: "셋째", b4: "넷째" };
/**
 * 옷도 골라야 한다 — **옷마다 인물이 프레임 안에서 다른 자리·다른 크기로 그려져 있어서**
 * 놓을 자리가 달라진다(우비는 몸이 더 작고 아래에 있다). 예전엔 여기서 기본옷만 보고
 * 계산해서, 관리자에서 맞춘 자리와 아이가 실제로 보는 자리가 달랐다.
 */
const OUTFITS: { id: string; label: string }[] = [
  { id: "o0", label: "기본" },
  { id: "o1", label: "겨울" },
  { id: "o2", label: "탐험가" },
  { id: "o3", label: "우비" },
];

interface PanelProps {
  lookKey?: string;
  /** 지금 게시본(방향 그림이 있는지 보고, 이 자리에서 올릴 수 있게). 없으면 올리기 칸을 감춘다. */
  assets?: Record<string, ManifestEntry | undefined>;
  partSpec?: AssetSpec;
  onUpload?: (key: string, entry: ManifestEntry | null) => void;
}

export function OverlayFitPanel({ lookKey, assets, partSpec, onUpload }: PanelProps) {
  const [manifest, setManifest] = useState<Manifest>({ version: 0, assets: {} });
  const [doc, setDoc] = useState<OverlayFitDoc>({});
  const [item, setItem] = useState<string>(heroPartKey("hair", "c1"));
  const [dir, setDir] = useState<OverlayDir>("down");
  const [body, setBody] = useState<string>(lookKey?.match(/b\d/)?.[0] ?? "b2");
  /** 켜면 지금 고른 아이에게만 적용된다(끄면 모든 아이). */
  const [onlyThis, setOnlyThis] = useState(false);
  const [outfit, setOutfit] = useState("o0");
  const [msg, setMsg] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [overlap, setOverlap] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([loadManifest(), loadOverlayFit()]).then(([m, d]) => {
      setManifest(m);
      setDoc(d);
    });
  }, []);

  useRegisterSave({
    id: "overlayfit",
    label: "모자·소품 자리",
    /**
     * 🔴 **바꾼 게 있다고 알린다.**
     *
     * 이걸 안 넘겨서 저장바의 「바꾼 것 N개」에도, 창을 닫을 때의 경고에도 이 화면이
     * 안 들어갔다(unsavedCount 가 dirty 를 센다). 자리를 한참 맞추고 새로고침하면
     * **아무 경고 없이** 통째로 날아갔다 — 운영자에게는 「저장했는데 초기화됐다」로 보인다
     * (2026-08-23 신고의 나머지 절반).
     */
    dirty: dirty ? 1 : 0,
    /**
     * 💾 웹에 올리지 않고 하던 일만. 자리 맞추기는 눈으로 몇 번씩 대 보는 일이라
     * 한 번에 안 끝난다 — 다른 화면과 같은 방식으로 이 기기에 담아 둔다.
     */
    draft: { data: () => doc, restore: (d) => setDoc(d as OverlayFitDoc) },
    save: async () => {
      try {
        await publishOverlayFit(doc);
        setMsg("✅ 저장했어요 — 열려 있는 화면에 바로 반영돼요");
        setDirty(false);
      } catch (e) {
        // 🔴 이유를 삼키지 않는다 — 판정은 한 곳(saveFailSay). **저장 바에도** 알린다 —
        //    화면에만 적고 끝내면 저장 바가 「✅ 웹에 올렸어요」를 띄운다(2026-09-18).
        const say = saveFailSay(e);
        setMsg(say);
        throw new Error(say);
      }
    },
  });

  // 운영자가 만든 모자·소품도 자리를 맞출 수 있어야 한다 — 매니페스트가 목록이다.
  const items = useMemo(() => overlayFitKeys(Object.keys(manifest.assets)), [manifest]);
  const key = overlayFitKeyFor(item, dir, onlyThis ? body : undefined);
  // 웹과 **같은 값**을 넘긴다(아이+옷). 아이만 넘기면 기본옷 기준으로 계산돼 화면과 어긋난다.
  const look = `${body}_${outfit}`;

  /**
   * 미리보기는 **화면에 실제로 나갈 값**(아이별 자동 보정 포함).
   * 슬라이더가 만지는 값과는 다르다 — 아래 `edited` 참고.
   */
  const shown = overlayFitOn(item, dir, look, doc);

  /**
   * ⚠️ 슬라이더가 만지는 값은 **저장할 키의 값**이어야 한다.
   *
   * 「모두」는 아이별 보정을 **하기 전의 기본값**을 고친다(그걸 코드 기본값 위에 덮어쓴다).
   * 여기에 화면 값(=고른 아이의 보정이 이미 들어간 값)을 저장하면, 그 아이의 보정치가
   * **모든 아이의 기본값**이 되어 다른 아이가 통째로 밀린다 — 셋째를 고르고 만졌는데
   * 첫째가 움직이던 버그가 이것이다. 게다가 렌더할 때 보정이 한 번 더 얹혀 이중으로 들어간다.
   *
   * 「이 아이만」은 저장값이 보정을 **대체**하므로 화면 값 그대로가 맞다.
   */
  const edited = onlyThis ? shown : overlayFitFor(item, dir, doc);
  const fit = edited;
  const hasOwn = Object.keys(doc).some((k) => k.startsWith(`${body}|`));
  /**
   * **이 항목·이 방향**을 따로 맞춰 둔 아이들.
   *
   * 「이 아이만」 값은 공용 값을 **대체**한다. 그래서 공용을 아무리 만져도 그 아이들은 꿈쩍
   * 않는다 — 화면에 ✎ 표시 하나뿐이라 "첫째만 바뀐다"로 보인다. 크게 알려 준다.
   */
  const overriding = BODIES.filter(
    (b) =>
      doc[overlayFitKeyFor(item, dir, b)] ??
      // 방향 값이 없으면 그 아이의 **정면 값**이 이 방향에도 쓰인다(렌더러와 같은 규칙) —
      // 그것도 공용 값을 가리므로 같이 알려야 한다.
      (dir !== "down" ? doc[overlayFitKeyFor(item, "down", b)] : undefined),
  );
  const hidden = (OVERLAY_HIDDEN[item] ?? []).includes(dir);
  /**
   * **웹이 이 조합을 안 그리는 경우.**
   *
   * 옷 그림에 이미 같은 게 그려져 있으면(겨울옷 털모자, 탐험가 옷 모자·가방) 아이 화면에는
   * 얹지 않는다(`heroOverlayKeys` → `overlayAllowed`). 그런데 여기서는 규칙을 모르고 얹어서
   * 보여 줬다 — 운영자가 겨울옷 위 모자를 정성껏 맞췄는데 메인화면은 꿈쩍도 안 했다.
   * 배관(API·실시간 푸시·계산)이 다 정상이라 원인을 찾기가 유난히 어려웠다.
   *
   * 관리자는 **아이가 보는 것과 같은 것**을 보여야 한다. 안 얹히면 여기서도 안 얹는다.
   */
  const cat = item.split(".")[1] as OverlayCategory;
  const blocked = !overlayAllowed(cat, outfit);
  const catLabel = cat === "hair" ? "모자" : cat === "acc" ? "소품" : "메달";

  /**
   * **이 방향에서 웹이 실제로 쓰는 그림.**
   *
   * 방향 그림(`<키>.side`·`<키>.up`)이 있으면 그걸, 없으면 앞모습을 쓴다(`overlayDirKeys`).
   * 예전엔 여기서 **항상 앞모습**을 얹어 보여 줬다 — 옆모습을 맞추는데 정면 모자가 얹혀 있으니
   * 어디에 놓아야 맞는지 알 수가 없었고, 방향 그림을 올려도 관리자에는 반영이 안 됐다.
   */
  /** 그림 출처: 위에서 내려준 **지금 편집 중인** 것이 있으면 그것(방금 올린 게 바로 보인다). */
  const artOf = assets ?? manifest.assets;

  const art = overlayDirKeys(item, dir);
  const artKey = art.find((k) => artOf[k]?.url);
  /** 이 방향 전용 그림이 없어 앞모습을 대신 쓰는 중 — 그러면 자리를 맞춰도 어색할 수밖에 없다. */
  const usingFront = dir !== "down" && art.length > 0 && artKey === item;
  const dirKey = `${item}.${dir}`; // 이 방향 전용 그림을 올릴 자리
  const itemUrl = blocked || !artKey ? undefined : artOf[artKey]?.url;
  const lookUrl =
    artOf[dir === "down" ? `hero.look.${look}` : `hero.look.${look}.${dir}`]?.url;

  /**
   * ⚙️ **자동 맞춤** — 그림을 재서 자리를 제안한다.
   *
   * 값을 **덮어쓰지 않고 넣기만** 한다(저장은 아래 [웹에 올리기]). 자동값이 늘 최선일 수는
   * 없으니 — 비대칭 물건·기울어진 그림 — 보고 나서 슬라이더로 다듬으면 그게 이긴다.
   *
   * 붙는 곳(머리·가슴·손)은 이름으로 다 못 가른다(소품이 배지인지 랜턴인지 모른다).
   * 그래서 짐작만 하고, 운영자가 고를 수 있게 세 버튼으로 둔다.
   */
  const [autoMsg, setAutoMsg] = useState<string | null>(null);
  const [autoBusy, setAutoBusy] = useState(false);
  async function autoFit(kind: AttachKind) {
    if (!itemUrl || autoBusy) return;
    setAutoBusy(true);
    setAutoMsg(null);
    try {
      const box = await measureArtBox(itemUrl);
      if (!box) {
        setAutoMsg("그림을 재지 못했어요. 다시 올린 뒤 눌러 주세요.");
        return;
      }
      if (!(box.w > 0)) {
        setAutoMsg("그림이 비어 있어요(전부 투명). 배경만 있는 파일은 아닌지 확인해 주세요.");
        return;
      }
      // 이 아이·이 방향의 실측 기준점. 없으면 평균 아이로(그래도 그럴듯한 자리에 온다)
      const anchor = HERO_ANCHOR[`${look}.${dir}`] ?? ANCHOR_REF;
      set(autoOverlayFit(box, kind, anchor));
      setAutoMsg("잰 값으로 맞췄어요. 보고 나서 아래 슬라이더로 다듬으세요.");
    } finally {
      setAutoBusy(false);
    }
  }

  function set(patch: Partial<OverlayFit>) {
    setDirty(true);
    setDoc((d) => ({
      ...d,
      [key]: { ...(onlyThis ? overlayFitOn(item, dir, look, d) : overlayFitFor(item, dir, d)), ...patch },
    }));
  }
  function reset() {
    setDirty(true);
    setDoc((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  }
  /** 이 아이에게 따로 맞춰 둔 값을 전부 지운다 → 다시 자동 보정으로 돌아간다. */
  function clearBody() {
    setDirty(true);
    setDoc((d) => Object.fromEntries(Object.entries(d).filter(([k]) => !k.startsWith(`${body}|`))));
  }

  return (
    <div className="ofit">
      <p className="hint">
        {onlyThis ? (
          <>
            지금 <b>{BODY_LABEL[body] ?? body} 아이에게만</b> 적용돼요. 나머지 아이는 안 바뀌어요.
          </>
        ) : (
          <>
            <b>모든 아이</b>에게 적용돼요 — 아이마다 머리 크기가 달라도 <b>자리는 저절로 맞춰져요</b>.
            그래도 어색한 아이가 있으면 아래 <b>「이 아이만」</b>을 켜세요.
          </>
        )}
      </p>
      {/* 이 조절이 **어느 히어로에 보이는지**. 완성형은 그림 한 장이라 아무것도 안 얹는다
          (`usesPresetArt`) — 그걸 모르면 "조절해도 메인화면이 안 바뀐다"로만 보인다. */}
      <p className="hint">
        이 조절은 <b>직접 꾸미기</b> 히어로에만 보여요. 완성형 히어로(탐험가 A~E)는 <b>그림 한 장</b>이라
        모자·소품을 얹지 않아요.
      </p>
      {msg ? <p className="console__publish">{msg}</p> : null}

      {/* ① 무엇을 맞출까 */}
      <div className="ofit__items">
        {items.map((k) => {
          const id = k.split(".").pop()!;
          return (
            <button
              key={k}
              type="button"
              className="ofit__item"
              data-on={k === item || undefined}
              onClick={() => setItem(k)}
            >
              {artOf[k]?.url ? <img src={artOf[k]!.url} alt="" /> : <span>?</span>}
              <small>{ITEM_LABEL[id] ?? id}</small>
            </button>
          );
        })}
      </div>

      {/* ② 어느 방향 / 어느 아이로 볼까 */}
      <div className="ofit__row">
        <div className="ofit__seg" role="group" aria-label="방향">
          {DIRS.map((d) => (
            <button key={d.id} type="button" data-on={dir === d.id || undefined} onClick={() => setDir(d.id)}>
              {d.label}
            </button>
          ))}
        </div>
        <div className="ofit__seg" role="group" aria-label="아이">
          {BODIES.map((b) => (
            <button key={b} type="button" data-on={body === b || undefined} onClick={() => setBody(b)}>
              {BODY_LABEL[b] ?? b}
              {Object.keys(doc).some((k) => k.startsWith(`${b}|`)) ? " ●" : ""}
            </button>
          ))}
        </div>
        <div className="ofit__seg" role="group" aria-label="옷">
          {OUTFITS.map((o) => (
            <button
              key={o.id}
              type="button"
              data-on={outfit === o.id || undefined}
              onClick={() => setOutfit(o.id)}
              /* 이 옷엔 안 얹는다는 걸 **누르기 전에** 알려 준다 */
              title={overlayAllowed(cat, o.id) ? undefined : `${o.label} 옷에는 이 ${catLabel}를 안 얹어요`}
            >
              {o.label}
              {overlayAllowed(cat, o.id) ? "" : " ✕"}
            </button>
          ))}
        </div>
        <label className="ofit__check">
          <input type="checkbox" checked={onlyThis} onChange={(e) => setOnlyThis(e.target.checked)} />
          이 아이만
        </label>
      </div>

      {blocked ? (
        <p className="ofit__warn ofit__warn--block">
          ⚠️ <b>{OUTFITS.find((o) => o.id === outfit)?.label} 옷</b>에는 {catLabel}가 <b>이미 그려져 있어요</b>.
          그래서 아이 화면에는 이 {catLabel}를 얹지 않아요 — <b>여기서 맞춰도 메인화면은 안 바뀝니다.</b>
          <br />
          위에서 <b>✕ 없는 옷</b>을 고르세요.
        </p>
      ) : null}

      {hidden ? (
        <p className="hint">🙈 이 방향에서는 <b>일부러 안 그려요</b> — 가슴에 다는 것은 뒤에서 안 보이니까요.</p>
      ) : (
        <>
          {/* ③ 미리보기 — 왼쪽이 실제 크기, 오른쪽이 확대 */}
          <div className="ofit__previews">
            <figure>
              <Preview look={lookUrl} item={itemUrl} fit={shown} px={PREVIEW_PX} />
              <figcaption>실제 크기 ({PREVIEW_PX}px)</figcaption>
            </figure>
            <figure>
              <Preview look={lookUrl} item={itemUrl} fit={shown} px={PREVIEW_PX * 3} />
              <figcaption>3배 확대</figcaption>
            </figure>
          </div>

          {/* ④ 네 아이 나란히 — 한 아이만 보고 맞추면 나머지가 망가진 걸 모른다 */}
          <div className="ofit__all">
            {BODIES.map((b) => {
              const url =
                artOf[dir === "down" ? `hero.look.${b}_${outfit}` : `hero.look.${b}_${outfit}.${dir}`]?.url;
              return (
                <figure key={b} data-on={b === body || undefined}>
                  <button type="button" onClick={() => setBody(b)} title={`${BODY_LABEL[b] ?? b} 아이 보기`}>
                    <Preview look={url} item={itemUrl} fit={overlayFitOn(item, dir, `${b}_${outfit}`, doc)} px={PREVIEW_PX} />
                  </button>
                  <figcaption>
                    {BODY_LABEL[b] ?? b}
                    {doc[overlayFitKeyFor(item, dir, b)] ? " ✎" : ""}
                  </figcaption>
                </figure>
              );
            })}
          </div>

          <OverlapCheck look={lookUrl} item={itemUrl} fit={shown} onResult={setOverlap} />
          {overlap ? <p className="hint">{overlap}</p> : null}

          {/* ⑤ 이 방향 전용 그림 — 없으면 앞모습을 돌려 쓰느라 자리를 아무리 맞춰도 어색하다.
              (옆에서 본 모자는 챙이 타원이고, 뒤에서는 리본이 안 보인다) */}
          {dir !== "down" && !blocked ? (
            <div className="ofit__dirart">
              <p className={usingFront ? "ofit__warn ofit__warn--block" : "hint"}>
                {usingFront ? (
                  <>
                    ⚠️ <b>{DIRS.find((d) => d.id === dir)?.label} 전용 그림이 없어요</b> — 지금은{" "}
                    <b>앞모습 그림을 돌려 쓰는 중</b>이라, 자리를 맞춰도 어색할 수 있어요.
                    옆에서 본 모자는 챙이 타원이고 뒤에서는 리본이 안 보이니까요.
                    <br />
                    아래에 올리면 <b>이 방향에서만</b> 쓰여요. 안 올려도 지금처럼 앞모습으로 나와요.
                  </>
                ) : (
                  <>✅ 이 방향 전용 그림을 쓰고 있어요.</>
                )}
              </p>
              {partSpec && onUpload ? (
                <SlotUploader
                  slotKey={dirKey}
                  label={`${ITEM_LABEL[item.split(".").pop()!] ?? item} · ${DIRS.find((d) => d.id === dir)?.label}`}
                  spec={partSpec}
                  entry={artOf[dirKey]}
                  onChange={onUpload}
                  onRemove={() => onUpload(dirKey, null)}
                />
              ) : null}
            </div>
          ) : null}

          {/* ④ 조절 */}
          {!onlyThis && overriding.length > 0 ? (
            <p className="ofit__warn">
              ⚠️ <b>{overriding.map((b) => BODY_LABEL[b] ?? b).join("·")}</b> 아이는 이 항목을
              <b> 따로 맞춰 둬서</b> 아래 기준값이 안 먹어요.
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => {
                  setDirty(true);
                  setDoc((d) => {
                    const next = { ...d };
                    for (const b of overriding) {
                      delete next[overlayFitKeyFor(item, dir, b)];
                      if (dir !== "down") delete next[overlayFitKeyFor(item, "down", b)];
                    }
                    return next;
                  });
                }}
              >
                따로 맞춘 값 지우고 기준값 따르기
              </button>
            </p>
          ) : null}
          <p className="hint">
            {onlyThis ? (
              <>
                아래 숫자는 <b>{BODY_LABEL[body] ?? body} 아이에게 그대로</b> 적용돼요(자동 맞춤 대신).
              </>
            ) : (
              <>
                아래 숫자는 <b>기준값</b>이에요 — 아이마다 머리 크기에 맞춰 자동으로 조금씩
                달라지니, 위 미리보기와 숫자가 똑같지 않을 수 있어요.
              </>
            )}
          </p>
          {/* 그림에서 재서 첫 값을 만든다 — 손조절은 그 다음이다.
              어디에 붙는 물건인지는 이름으로 못 가르니 운영자가 고른다. */}
          <div className="ofit__auto">
            <b>⚙️ 자동 맞춤</b>
            <span className="hint">그림을 재서 자리를 잡아요. 어디에 붙나요?</span>
            <div className="ofit__auto-btns">
              <button type="button" className="btn btn--sm" disabled={autoBusy || !itemUrl} onClick={() => void autoFit("head")}>
                🎩 머리에
              </button>
              <button type="button" className="btn btn--sm" disabled={autoBusy || !itemUrl} onClick={() => void autoFit("chest")}>
                🎖 가슴에
              </button>
              <button type="button" className="btn btn--sm" disabled={autoBusy || !itemUrl} onClick={() => void autoFit("hand")}>
                🏮 손에 쥐게
              </button>
            </div>
            {autoMsg ? <p className="hint">{autoMsg}</p> : null}
          </div>

          <div className="ofit__ctrls">
            <Slider label="크기" value={fit.scale} min={OVERLAY_FIT_LIMITS.scaleMin} max={OVERLAY_FIT_LIMITS.scaleMax}
              step={0.01} onChange={(v) => set({ scale: v })} />
            <Slider label="좌우" value={fit.dx} min={-OVERLAY_FIT_LIMITS.offset} max={OVERLAY_FIT_LIMITS.offset}
              step={0.005} onChange={(v) => set({ dx: v })} />
            <Slider label="위아래" value={fit.dy} min={-OVERLAY_FIT_LIMITS.offset} max={OVERLAY_FIT_LIMITS.offset}
              step={0.005} onChange={(v) => set({ dy: v })} />
            <Slider label="기울기(°)" value={fit.rot} min={-OVERLAY_FIT_LIMITS.rot} max={OVERLAY_FIT_LIMITS.rot}
              step={1} onChange={(v) => set({ rot: v })} digits={0} />
            <label className="ofit__check">
              <input type="checkbox" checked={fit.flipX} onChange={(e) => set({ flipX: e.target.checked })} />
              좌우 뒤집기
            </label>
          </div>

          <div className="ofit__acts">
            <button type="button" className="btn btn--sm" onClick={reset}>
              {onlyThis ? `${BODY_LABEL[body] ?? body} 아이 값 지우기` : "이 방향 값 지우기(기본으로)"}
            </button>
            {!onlyThis && hasOwn ? (
              <button type="button" className="btn btn--sm" onClick={clearBody}>
                {BODY_LABEL[body] ?? body} 아이의 따로 맞춘 값 전부 지우기
              </button>
            ) : null}
            {dir !== "down" ? (
              <button
                type="button"
                className="btn btn--sm"
                onClick={() =>
                  set({ ...(onlyThis ? overlayFitOn(item, "down", look, doc) : overlayFitFor(item, "down", doc)) })
                }
              >
                앞모습 값 복사해 오기
              </button>
            ) : null}
            {dirty ? <span className="hint">● 저장 안 됨 — 아래 저장 바에서 저장하세요</span> : null}
          </div>
        </>
      )}
    </div>
  );
}

/** 아이 위에 물건을 얹어 보여준다 — 웹 렌더러와 **같은 계산**(중심 기준 scale·이동·회전·반전). */
function Preview({ look, item, fit, px }: { look?: string; item?: string; fit: OverlayFit; px: number }) {
  const t = `translate(${fit.dx * px}px, ${fit.dy * px}px) rotate(${fit.rot}deg) scale(${
    fit.scale * (fit.flipX ? -1 : 1)
  }, ${fit.scale})`;
  return (
    <div className="ofit__stage" style={{ width: px, height: px }}>
      {look ? <img src={look} alt="" style={{ width: px, height: px }} /> : null}
      {item ? <img src={item} alt="" style={{ width: px, height: px, transform: t }} /> : null}
    </div>
  );
}

/**
 * 몸과 얼마나 겹치는지 재서 알려 준다.
 *
 * 왜 필요한가: 눈으로 맞추면 **뒷모습에서 물건이 허공에 뜨는 걸 놓친다.** 실제로 그랬다
 * (랜턴 겹침 0%·몸 밖 61px). 사람 눈보다 픽셀이 정확하다.
 * 이미지 출처 제한으로 못 읽으면 조용히 접는다 — 검사 못 한다고 편집을 막을 이유는 없다.
 */
function OverlapCheck({
  look,
  item,
  fit,
  onResult,
}: {
  look?: string;
  item?: string;
  fit: OverlayFit;
  onResult: (s: string | null) => void;
}) {
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!look || !item) return onResult(null);
    if (timer.current) window.clearTimeout(timer.current);
    // 슬라이더를 끄는 동안 매번 재면 느리다 — 멈춘 뒤에 한 번
    timer.current = window.setTimeout(() => {
      void measure(look, item, fit).then(onResult);
    }, 220);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fit 은 낱낱이 적어 뒀다(통째로 넣으면 매 렌더 다시 잰다). onResult 는 부모가 새로 만든다
  }, [look, item, fit.scale, fit.dx, fit.dy, fit.rot, fit.flipX]);
  return null;
}

const SIZE = 256; // 검사용 캔버스 — 실제 표시보다 크게 잡아야 몇 픽셀 차이가 안 묻힌다

async function measure(lookUrl: string, itemUrl: string, fit: OverlayFit): Promise<string | null> {
  try {
    const [look, item] = await Promise.all([loadImg(lookUrl), loadImg(itemUrl)]);
    const body = mask(draw(look, null));
    const ov = mask(draw(item, fit));
    let on = 0;
    let hit = 0;
    for (let i = 0; i < ov.length; i++) {
      if (!ov[i]) continue;
      on++;
      if (body[i]) hit++;
    }
    if (on === 0) return null;
    const pct = Math.round((hit / on) * 100);
    if (pct < 15) return `⚠️ 몸과 ${pct}% 만 겹쳐요 — 들고 있는 게 아니라 옆에 떠 보일 수 있어요.`;
    if (pct < 40) return `몸과 ${pct}% 겹쳐요.`;
    return `✅ 몸과 ${pct}% 겹쳐요.`;
  } catch {
    return null; // 교차출처 등 — 검사만 못 할 뿐 편집은 계속된다
  }
}

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous"; // /assets 는 CORS 를 열어 둔다 — 없으면 캔버스가 오염돼 읽을 수 없다
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
}

function draw(img: HTMLImageElement, fit: OverlayFit | null): ImageData {
  const c = document.createElement("canvas");
  c.width = SIZE;
  c.height = SIZE;
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  if (fit) {
    ctx.translate(SIZE / 2 + fit.dx * SIZE, SIZE / 2 + fit.dy * SIZE);
    ctx.rotate((fit.rot * Math.PI) / 180);
    ctx.scale(fit.scale * (fit.flipX ? -1 : 1), fit.scale);
    ctx.drawImage(img, -SIZE / 2, -SIZE / 2, SIZE, SIZE);
  } else {
    ctx.drawImage(img, 0, 0, SIZE, SIZE);
  }
  return ctx.getImageData(0, 0, SIZE, SIZE);
}

function mask(d: ImageData): Uint8Array {
  const m = new Uint8Array(SIZE * SIZE);
  for (let i = 0; i < m.length; i++) m[i] = d.data[i * 4 + 3]! > 32 ? 1 : 0;
  return m;
}

function Slider({
  label, value, min, max, step, onChange, digits = 3,
}: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; digits?: number;
}) {
  return (
    <label className="ofit__slider">
      <span>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))} />
      <b>{value.toFixed(digits)}</b>
    </label>
  );
}
